import { apiClient } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiResponse } from "@/api/types";
import type {
  AuthResponse,
  AuthUser,
  FirebaseWebConfig,
  LoginPayload,
  RegisterPayload,
  SendOtpPayload,
  SendOtpResponse,
  VerifyOtpPayload,
  VerifyOtpResponse,
} from "@/features/auth/types";

/**
 * All auth HTTP calls live here. Components and hooks never touch axios
 * directly — they call these functions instead.
 */
export const authService = {
  /**
   * `POST /auth/login` sets the session as httpOnly cookies and returns
   * `{ success, data: { user } }` — no token in the body.
   */
  async login(payload: LoginPayload): Promise<AuthResponse> {
    const { data } = await apiClient.post<ApiResponse<{ user: AuthUser }>>(endpoints.auth.login, payload);
    return { user: data.data.user };
  },

  /**
   * Staff email+password register — `POST /auth/register`. Returns the
   * created user directly as `data.data`; does NOT set a session cookie.
   */
  async register(payload: RegisterPayload): Promise<AuthResponse> {
    const { data } = await apiClient.post<ApiResponse<AuthUser>>(endpoints.auth.register, payload);
    return { user: data.data };
  },

  async logout(): Promise<void> {
    await apiClient.post(endpoints.auth.logout);
  },

  async getMe(): Promise<AuthUser> {
    const { data } = await apiClient.get<ApiResponse<AuthUser>>(endpoints.auth.me);
    return data.data;
  },

  // ------------------------------------------------------------------
  // Firebase Phone Auth
  // ------------------------------------------------------------------

  /**
   * Backend Firebase Web SDK config — used by `src/lib/firebase.ts` to
   * initialize the client SDK. Cached in-memory by the caller so this only
   * hits the network on first phone-auth use per page load.
   */
  async getFirebaseConfig(): Promise<FirebaseWebConfig> {
    const { data } = await apiClient.get<ApiResponse<FirebaseWebConfig>>(
      endpoints.publicConfig.firebase,
    );
    return data.data;
  },

  /**
   * `POST /auth/send-otp` — pre-flight rate-limit gate. Backend does NOT
   * actually send the SMS (that's Firebase's job via the client SDK);
   * this just enforces the resend cooldown per phone number.
   */
  async sendOtp(payload: SendOtpPayload): Promise<SendOtpResponse> {
    const { data } = await apiClient.post<ApiResponse<SendOtpResponse>>(
      endpoints.auth.sendOtp,
      payload,
    );
    return data.data;
  },

  /**
   * `POST /auth/verify-otp` — server-side check of the Firebase ID token.
   *   • intent=LOGIN + existing user → session cookies set, returns { status: "LOGGED_IN", user }
   *   • intent=REGISTER (or new phone) → Redis marker set, returns { status: "VERIFIED_NEW_PHONE", phone }
   */
  async verifyOtp(payload: VerifyOtpPayload): Promise<VerifyOtpResponse> {
    const { data } = await apiClient.post<ApiResponse<VerifyOtpResponse>>(
      endpoints.auth.verifyOtp,
      payload,
    );
    return data.data;
  },
};
